import React, {useState} from "react"
import {Box} from "@mui/system"
import ButtonWithIcon from "src/v2/components/Button/Button"
import PropTypes from "prop-types"
import {ReactComponent as CheckIcon} from "src/v2/assets/icons/check-icon.svg"
import {Divider, Grid, ThemeProvider, Typography} from "@mui/material"
import theme from "src/v2/theme/theme"
import {useBoxSectionRating} from "../../hooks/use-ratings-use-reducer"
import {useRatingEmissaoTable} from "../../hooks/table-emission-hooks/use-last-rating-emission.js"
import ModalDialogPage from "src/components/modal-dialog"
import ParameterTable from "src/components/table/parameter-table"
import AlertMessage from "src/components/alert-message"
import TableGray from "src/components/table/table-gray"
import {
    renderCellDate,
    renderTextColumn,
} from "src/utils/table-render-functions"
import themeLegacy from "src/utils/theme"
import dayjs from "dayjs"
import {DATE_FORMAT_SHOW} from "src/utils/constants"
import {formatStringToFloatDecimalCase} from "src/utils/format-decimal"
import {usePrecosProcessados} from "../../hooks/table-emission-hooks/use-precos-processados"
import {useRatingDevedorTable} from "../../hooks/table-emission-hooks/use-last-debtor-table"
import {useIndiceLiquidezTable} from "../../hooks/table-emission-hooks/use-last-index-liquidity"
import {useUltimoNegocioTable} from "../../hooks/table-emission-hooks/use-last-business"
import {useUltimoEventoCreditoEmissao} from "../../hooks/table-emission-hooks/use-last-event-credit-emission"
import {useUltimoEventoCreditoDevedor} from "../../hooks/table-emission-hooks/use-last-event-debtor"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import {formatNumber} from "src/v2/utils/formatter/format-number"

const EmissionSections = ({title, onClick, children}) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "row",
            height: "auto",
            width: "fit-content",
        }}
    >
        <Divider
            orientation="vertical"
            flexItem
            sx={{
                width: 2,
                marginRight: "22px",
                py: 3,
                backgroundColor: theme.palette.common.white,
                border: "none",
                "&::before, &::after": {border: "none"},
            }}
        />
        <Box
            sx={{
                flex: 1,
                gap: 2,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Typography variant="textLarge" sx={{color: "common.white"}}>
                {title}
            </Typography>
            {children}
            <Box sx={{maxWidth: "344px"}}>
                <ButtonWithIcon onClick={onClick} icon={<CheckIcon/>}>
                    MOSTRAR TODOS
                </ButtonWithIcon>
            </Box>
        </Box>
    </Box>
)

EmissionSections.propTypes = {
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    children: PropTypes.node,
}

const LabelSectionItem = ({title, description}) => (
    <Box display="flex" gap="5px" flexDirection="column">
        <Typography variant="contraHeading" color="#FFFFFFB2">
            {title}
        </Typography>
        <Typography variant="textBody" color="#FFF">
            {description || ""}
        </Typography>
    </Box>
)

LabelSectionItem.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
}

const EmptyLabelSectionItem = ({title}) => (
    <Box display="flex">
        <Typography variant="textBody" color="#FFF">
            {`Não há ${title} para o CETIP consultado` || ""}
        </Typography>
    </Box>
)

EmptyLabelSectionItem.propTypes = {
    title: PropTypes.string.isRequired,
}

export const BoxSectionRating = ({
                                     cetipIsin,
                                     emissaoService,
                                     characterData,
                                     admin,
                                 }) => {
    const cpfCnpj = characterData?.CPF_CNPJ_Devedor
    const [modalIsVisible, setModalIsVisible] = useState(false)
    const [modalType, setModalType] = useState("")
    const {state} = useBoxSectionRating(cetipIsin, cpfCnpj, emissaoService)

    const {state: stateRating, actions: ratingActions} =
        useRatingEmissaoTable(emissaoService, cetipIsin, characterData)
    const {state: statePriceProcess, actions: priceProcessActions} =
        usePrecosProcessados(emissaoService, cetipIsin, characterData)

    const {state: stateDebtorEmission, actions: debtorEmissionActions} =
        useRatingDevedorTable(emissaoService, cpfCnpj, characterData)

    const {state: stateIndexLiquidity, actions: indexLiquidityActions} =
        useIndiceLiquidezTable(emissaoService, cetipIsin, characterData)

    const {state: lastBusinessState, actions: lastBusinessActions} =
        useUltimoNegocioTable(emissaoService, cetipIsin, characterData)

    const {
        state: lastEventCreditEmissionState,
        actions: lastEventCreditEmissionActions,
    } = useUltimoEventoCreditoEmissao(emissaoService, cetipIsin, characterData)

    const {
        state: lastEventCreditDebtorState,
        actions: lastEventCreditDebtorActions,
    } = useUltimoEventoCreditoDevedor(
        emissaoService,
        cpfCnpj,
        characterData,
        cetipIsin
    )

    const isLoading =
        state.loading ||
        stateRating.loading ||
        statePriceProcess.loading ||
        stateDebtorEmission.loading ||
        stateIndexLiquidity.loading ||
        lastBusinessState.loading ||
        lastEventCreditEmissionState.loading ||
        lastEventCreditDebtorState.loading

    const modalConfig = {
        last_emission: {
            title: "Histórico Rating Emissão",
            btnAddTitle: "ADICIONAR RATING",
            loadData: async () => await ratingActions.loadTableRows(),
            table: stateRating.table,
            rowHeight: "auto",
            columns: [
                {
                    field: "DataReferencia",
                    typeComponent: "date",
                    headerName: "Data Referencia",
                    minWidth: 130,
                    editable: admin,
                    renderCell: renderCellDate,
                },
                {
                    field: "DataValidade",
                    typeComponent: "date",
                    headerName: "Data Validade",
                    minWidth: 130,
                    editable: admin,
                    renderCell: renderCellDate,
                },
                {
                    field: "Fonte",
                    headerName: "Fonte",
                    minWidth: 100,
                    editable: admin,
                },
                {
                    field: "ScoreRating",
                    headerName: "Score Rating",
                    minWidth: 100,
                    editable: admin,
                },
                {
                    field: "Rating",
                    headerName: "Rating",
                    minWidth: 100,
                    editable: admin,
                },
                {
                    field: "Sinal",
                    headerName: "Sinal",
                    minWidth: 100,
                    editable: admin,
                },
                {
                    field: "Perspectiva",
                    headerName: "Perspectiva",
                    minWidth: 100,
                    editable: admin,
                },
                {
                    field: "Observacao",
                    headerName: "Observação",
                    minWidth: stateRating.minWidthObservacao,
                    editable: admin,
                    renderCell: renderTextColumn,
                },
                {
                    field: "AnaliseCreditoEmissao",
                    headerName: "Análise Crédito Emissão",
                    minWidth: stateRating.minWidthAnaliseCredito,
                    editable: admin,
                    renderCell: renderTextColumn,
                },
            ],
            rows: stateRating.rows,
            alert: stateRating.alert,
            actions: {
                addRow: ratingActions.addRow,
                deleteRow: ratingActions.deleteRow,
                updateRow: ratingActions.updateRow,
                closeAlert: ratingActions.closeAlert,
            },
        },
        last_debtor: {
            title: "Histórico Rating Devedor",
            btnAddTitle: "ADICIONAR RATING",
            loadData: async () => await debtorEmissionActions.loadTableRows(),
            table: stateDebtorEmission.table,
            rowHeight: "auto",

            columns: [
                {
                    field: "DataReferencia",
                    minWidth: 130,
                    headerName: "Data Referencia",
                    flex: 1,
                    editable: admin,
                    renderCell: renderCellDate,
                },
                {
                    field: "DataValidade",
                    minWidth: 130,
                    headerName: "Data Validade",
                    flex: 1,
                    editable: admin,
                    renderCell: renderCellDate,
                },
                {
                    field: "Fonte",
                    minWidth: 100,
                    headerName: "Fonte",
                    flex: 1,
                    editable: admin,
                },
                {
                    field: "ScoreRating",
                    minWidth: 100,
                    headerName: "Score Rating",
                    flex: 1,
                    editable: admin,
                },
                {
                    field: "Rating",
                    minWidth: 100,
                    headerName: "Rating",
                    flex: 1,
                    editable: admin,
                },
                {
                    field: "Sinal",
                    minWidth: 100,
                    headerName: "Sinal",
                    flex: 1,
                    editable: admin,
                },
                {
                    field: "Perspectiva",
                    minWidth: 100,
                    headerName: "Perspectiva",
                    flex: 1,
                    editable: admin,
                },
                {
                    field: "Observacao",
                    headerName: "Observação",
                    flex: 1,
                    minWidth: "fit-content",
                    editable: admin,
                    renderCell: renderTextColumn,
                },
                {
                    field: "AnaliseCreditoDevedor",
                    headerName: "Análise Crédito Devedor",
                    flex: 1,
                    minWidth: "fit-content",
                    editable: admin,
                    renderCell: renderTextColumn,
                },
            ],
            rows: stateDebtorEmission.rows,
            alert: stateDebtorEmission.alert,
            actions: {
                addRow: debtorEmissionActions.addRow,
                deleteRow: debtorEmissionActions.deleteRow,
                updateRow: debtorEmissionActions.updateRow,
                closeAlert: debtorEmissionActions.closeAlert,
            },
        },
        history_event_credit_emission: {
            title: "Histórico Evento de Crédito Emissão",
            btnAddTitle: "ADICIONAR RATING",
            table: lastEventCreditEmissionState.table,
            rowHeight: "auto",

            loadData: async () =>
                await lastEventCreditEmissionActions.loadTableRows(),
            columns: [
                {
                    field: "DataReferencia",
                    headerName: "Data Referencia",
                    minWidth: 150,
                    flex: 1,
                    editable: admin,
                    renderCell: renderCellDate,
                },
                {
                    field: "EventoCreditoEmissao",
                    headerName: "Evento",
                    flex: 1,
                    minWidth: "min-content",
                    editable: admin,
                    renderCell: renderTextColumn,
                },
                {
                    field: "ScoreEvento",
                    headerName: "Score Evento",
                    editable: admin,
                    flex: 1,
                    minWidth: 120,
                },
            ],
            rows: lastEventCreditEmissionState.rows,
            alert: lastEventCreditEmissionState.alert,
            actions: {
                addRow: lastEventCreditEmissionActions.addRow,
                deleteRow: lastEventCreditEmissionActions.deleteRow,
                updateRow: lastEventCreditEmissionActions.updateRow,
                closeAlert: lastEventCreditEmissionActions.closeAlert,
            },
        },

        index_liquidity: {
            title: "Histórico Índice Liquidez",
            btnAddTitle: "ADICIONAR ÍNDICE LIQUIDEZ",
            table: stateIndexLiquidity.table,
            rowHeight: undefined,

            loadData: async () => await indexLiquidityActions.loadTableRows(),
            columns: [
                {
                    field: "DataReferencia",
                    headerName: "Data Referência",
                    flex: 1,
                    renderCell: renderCellDate,
                },
                {
                    field: "IndiceLiquidez",
                    headerName: "Índice Liquidez",
                    flex: 1,
                },
            ],
            rows: stateIndexLiquidity.rows,
            alert: stateIndexLiquidity.alert,
            actions: {
                addRow: indexLiquidityActions.addRow,
                deleteRow: indexLiquidityActions.deleteRow,
                updateRow: indexLiquidityActions.updateRow,
                closeAlert: indexLiquidityActions.closeAlert,
            },
        },

        history_last_negocitacions: {
            title: "Histórico Das Últimas Negociações",
            btnAddTitle: "",
            table: lastBusinessState.table,
            disableDeleteRow: true,
            rowHeight: undefined,

            loadData: async () => await lastBusinessActions.loadTableRows(),
            columns: [
                {
                    field: "DataReferencia",
                    headerName: "Data Referencia",
                    flex: 1,
                    renderCell: (params) =>
                        dayjs(params.value).format(DATE_FORMAT_SHOW),
                },
                {
                    field: "PU_MTM",
                    headerName: "PU MTM",
                    flex: 1,
                    renderCell: (params) =>
                        formatStringToFloatDecimalCase(params.value, 4, 4),
                },
                {
                    field: "PercentualPUPAR",
                    headerName: "%PUPAR",
                    flex: 1,
                    renderCell: (params) =>
                        formatStringToFloatDecimalCase(params.value, 2, 2),
                },
                {
                    field: "TaxaMTM",
                    headerName: "Taxa MTM",
                    flex: 1,
                    renderCell: (params) =>
                        formatStringToFloatDecimalCase(params.value, 4, 4),
                },
                {
                    field: "SpreadCreditoFinal",
                    headerName: "Spread Crédito",
                    flex: 1,
                    renderCell: (params) =>
                        formatStringToFloatDecimalCase(params.value, 4, 4),
                },
                {
                    field: "VolumeFinanceiro",
                    headerName: "Volume Financeiro",
                    flex: 1,
                    renderCell: (params) =>
                        formatStringToFloatDecimalCase(params.value, 2, 2),
                },
            ],
            rows: lastBusinessState.rows,
            alert: lastBusinessState.alert,
            actions: {
                // addRow: lastBusinessActions.addRow,
                // deleteRow: lastBusinessActions.deleteRow,
                updateRow: lastBusinessActions.updateRow,
                closeAlert: lastBusinessActions.closeAlert,
            },
        },

        history_event_credit_debtor: {
            title: "Histórico Evento de Crédito Devedor",
            btnAddTitle: "ADICIONAR RATING",
            table: lastEventCreditDebtorState.table,
            rowHeight: "auto",

            loadData: async () =>
                await lastEventCreditDebtorActions.loadTableRows(),
            columns: [
                {
                    field: "DataReferencia",
                    headerName: "Data Referencia",
                    flex: 1,
                    editable: admin,
                    renderCell: renderCellDate,
                },
                {
                    field: "EventoCreditoDevedor",
                    headerName: "Evento",
                    flex: 1,
                    minWidth: "min-content",
                    editable: admin,
                    renderCell: renderTextColumn,
                },
                {
                    field: "ScoreEvento",
                    headerName: "Score Evento",
                    flex: 1,
                    editable: admin,
                },
            ],
            rows: lastEventCreditDebtorState.rows,
            alert: lastEventCreditDebtorState.alert,
            actions: {
                addRow: lastEventCreditDebtorActions.addRow,
                deleteRow: lastEventCreditDebtorActions.deleteRow,
                updateRow: lastEventCreditDebtorActions.updateRow,
                closeAlert: lastEventCreditDebtorActions.closeAlert,
            },
        },

        tax_indicative: {
            title: "Histórico Taxa Indicativa",
            btnAddTitle: "ADICIONAR PREÇOS PROCESSADOS",
            loadData: async () => await priceProcessActions.loadTableRows(),
            table: statePriceProcess.table,
            rowHeight: undefined,

            columns: [
                {
                    field: "DataReferencia",
                    headerName: "Data Referência",
                    flex: 1,
                    renderCell: renderCellDate,
                    editable: admin,
                },
                {
                    field: "TaxaMTM",
                    headerName: "Taxa MTM",
                    flex: 1,
                    editable: admin,
                    renderCell: ({value}) =>
                        formatStringToFloatDecimalCase(value, 3, 3),
                },
                {
                    field: "PercentualPUPAR",
                    headerName: "Percentual PUPAR",
                    flex: 1,
                    editable: admin,
                    renderCell: ({value}) =>
                        formatStringToFloatDecimalCase(value, 3, 3),
                },
                {
                    field: "SpreadCredito",
                    headerName: "Spread Crédito",
                    flex: 1,
                    editable: admin,
                    renderCell: ({value}) =>
                        formatStringToFloatDecimalCase(value, 3, 3),
                },
            ],
            rows: statePriceProcess.rows,
            alert: statePriceProcess.alert,
            actions: {
                addRow: priceProcessActions.addRow,
                deleteRow: priceProcessActions.deleteRow,
                updateRow: priceProcessActions.updateRow,
                closeAlert: priceProcessActions.closeAlert,
            },
        },
    }

    const handleClickRatingEmission = async (value) => {
        if (!modalConfig[value]) {
            return
        }
        modalConfig[value].loadData()

        setModalType(value)
        setModalIsVisible(true)
    }

    return (
        <>
            {isLoading && (
                <LoaderOverlay>
                    <Loader/>
                </LoaderOverlay>
            )}
            <ThemeProvider theme={themeLegacy}>
                <ModalDialogPage
                    open={modalIsVisible}
                    title={modalType ? modalConfig[modalType].title : ""}
                    close={() => {
                        setModalIsVisible(false)
                    }}
                >
                    <>
                        {/* <Loading show={stateRating.loading} /> */}
                        <Grid container direction="column" spacing={2}>
                            <Grid item xs={12} md={12} xl={12}>
                                <TableGray
                                    data={
                                        Array.isArray(
                                            modalConfig?.[modalType]?.table
                                        )
                                            ? modalConfig[modalType].table
                                            : []
                                    }
                                />

                                <Grid item xs={12} style={{marginTop: 8}}>
                                    <AlertMessage
                                        open={
                                            modalType
                                                ? modalConfig[modalType]?.alert
                                                    ?.open
                                                : false
                                        }
                                        message={
                                            modalType
                                                ? modalConfig[modalType]?.alert
                                                    ?.message
                                                : ""
                                        }
                                        type={
                                            modalType
                                                ? modalConfig[modalType]?.alert
                                                    ?.type
                                                : undefined
                                        }
                                        close={
                                            modalType
                                                ? modalConfig[modalType]?.actions?.closeAlert
                                                : undefined
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <br/>
                        <ParameterTable
                            rowHeigth={
                                modalType
                                    ? modalConfig[modalType]?.rowHeight
                                    : undefined
                            }
                            columns={
                                modalType ? modalConfig[modalType].columns : []
                            }
                            data={
                                modalType
                                    ? (modalConfig[modalType]?.rows ?? [])
                                    : []
                            }
                            createRow={
                                modalType
                                    ? modalConfig[modalType].actions.addRow
                                    : () => {
                                    }
                            }
                            setData={
                                modalType
                                    ? modalConfig[modalType].actions.addRow
                                    : () => {
                                    }
                            }
                            deleteRow={
                                modalType
                                    ? modalConfig[modalType].actions.deleteRow
                                    : () => {
                                    }
                            }
                            updateRow={
                                modalType
                                    ? modalConfig[modalType].actions.updateRow
                                    : () => {
                                    }
                            }
                            loadData={
                                modalType
                                    ? modalConfig[modalType].loadData
                                    : async () => {
                                    }
                            }
                            disableDeleteColumn={
                                !admin ||
                                modalConfig[modalType]?.disableDeleteRow
                            }
                            disableAddRow={!admin}
                            buttonAddVariant={"contained"}
                            buttonAddTitle={
                                modalType
                                    ? modalConfig[modalType]?.btnAddTitle
                                    : ""
                            }
                        />
                    </>
                </ModalDialogPage>
            </ThemeProvider>

            <Box>
                <Grid container rowSpacing={3}>
                    <Grid item xxxs={4} sm={6} lg={4}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission("last_emission")
                            }
                            title={"Último Rating Emissão"}
                        >
                            {state.isEmpty.ultimoRatingEmissao ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="rating de emissão"/>
                                </Grid>
                            ) : (
                                <>
                                    <Box display="flex" gap="34px">
                                        <LabelSectionItem
                                            title="DATA VALIDADE"
                                            description={
                                                state.ultimoRatingEmissao
                                                    ?.data_validade
                                            }
                                        />
                                        <LabelSectionItem
                                            title="FONTE"
                                            description={
                                                state.ultimoRatingEmissao?.fonte
                                            }
                                        />
                                        <LabelSectionItem
                                            title="RATING"
                                            description={
                                                state.ultimoRatingEmissao
                                                    ?.rating
                                            }
                                        />
                                    </Box>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>

                    <Grid item xxxs={4} sm={6} lg={4}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission("last_debtor")
                            }
                            title={"Último Rating Devedor"}
                        >
                            {state.isEmpty.ultimoRatingDevedor ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="rating devedor"/>
                                </Grid>
                            ) : (
                                <>
                                    <Box display="flex" gap="34px">
                                        <LabelSectionItem
                                            title="DATA VALIDADE"
                                            description={
                                                state.ultimoRatingDevedor
                                                    ?.data_validade
                                            }
                                        />
                                        <LabelSectionItem
                                            title="FONTE"
                                            description={
                                                state.ultimoRatingDevedor?.fonte
                                            }
                                        />
                                        <LabelSectionItem
                                            title="RATING"
                                            description={
                                                state.ultimoRatingDevedor
                                                    ?.rating
                                            }
                                        />
                                    </Box>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>

                    <Grid item xxxs={4} sm={6} lg={4}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission("index_liquidity")
                            }
                            title={"Último Indice de Liquidez"}
                        >
                            {state.isEmpty.ultimoIndiceLiquidez ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="índice de liquidez"/>
                                </Grid>
                            ) : (
                                <>
                                    <Box display="flex" gap="34px">
                                        <LabelSectionItem
                                            title="DATA REFERENCIA"
                                            description={
                                                state.ultimoIndiceLiquidez?.data_referencia
                                            }
                                        />
                                        <LabelSectionItem
                                            title="INDICE LIQUIDEZ"
                                            description={formatNumber(state.ultimoIndiceLiquidez?.indice_liquidez, 4)}
                                        />
                                    </Box>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>

                    <Grid item xxxs={4} sm={6} lg={4}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission("tax_indicative")
                            }
                            title={"Última Taxa Indicativa"}
                        >
                            {state.isEmpty.ultimoPrecosProcessados ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="taxa indicativa"/>
                                </Grid>
                            ) : (
                                <>
                                    <Box display="flex" gap="34px">
                                        <LabelSectionItem
                                            title="DATA REFERENCIA"
                                            description={
                                                state.ultimoPrecosProcessados
                                                    ?.data_referencia
                                            }
                                        />
                                        <LabelSectionItem
                                            title="SPREAD CRÉDITO"
                                            description={formatNumber(
                                                state.ultimoPrecosProcessados
                                                    ?.spread_credito,
                                                4
                                            )}
                                        />
                                        <LabelSectionItem
                                            title="% PUPAR"
                                            description={formatNumber(
                                                state.ultimoPrecosProcessados
                                                    ?.percentual_pupar,
                                                4
                                            )}
                                        />
                                    </Box>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>

                    <Grid item xxxs={4} sm={6} lg={4} sx={{display: "flex"}}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission(
                                    "history_last_negocitacions"
                                )
                            }
                            title={"Últimos Negócios"}
                            sx={{flex: 1}}
                        >
                            {state.isEmpty.ultimoNegocio ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="negócios"/>
                                </Grid>
                            ) : (
                                <>
                                    <Box display="flex" gap="34px">
                                        <LabelSectionItem
                                            title="DATA REFERENCIA"
                                            description={
                                                state.ultimoNegocio
                                                    ?.data_referencia
                                            }
                                        />
                                        <LabelSectionItem
                                            title="% PUPAR"
                                            description={formatNumber(
                                                state.ultimoNegocio?.pupar,
                                                4
                                            )}
                                        />
                                        <LabelSectionItem
                                            title="SPREAD CRÉDITO"
                                            description={formatNumber(
                                                state.ultimoNegocio
                                                    ?.spread_credito,
                                                4
                                            )}
                                        />
                                    </Box>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>

                    <Grid item lg={4}/>

                    <Grid item xxxs={4} sm={6} lg={4} sx={{display: "flex"}}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission(
                                    "history_event_credit_emission"
                                )
                            }
                            title={"Último Evento de Crédito Emissão"}
                            sx={{flex: 1}}
                        >
                            {state.isEmpty.ultimoCreditoEmissao ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="evento de crédito de emissão"/>
                                </Grid>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item md={6} lg={5}>
                                            <LabelSectionItem
                                                title="DATA REFERENCIA"
                                                description={
                                                    state.ultimoCreditoEmissao
                                                        ?.data_referencia
                                                }
                                            />
                                        </Grid>
                                        <Grid item md={6} lg={4}>
                                            <LabelSectionItem
                                                title="SCORE EVENTO"
                                                description={formatNumber(state.ultimoCreditoEmissao?.score_evento, 4)}
                                            />
                                        </Grid>
                                        <Grid item md={8} lg={8}>
                                            <LabelSectionItem
                                                title="EVENTO CRÉDITO"
                                                description={
                                                    state.ultimoCreditoEmissao
                                                        ?.evento_credito
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>

                    <Grid item xxxs={4} sm={4} lg={4} sx={{display: "flex"}}>
                        <EmissionSections
                            onClick={() =>
                                handleClickRatingEmission(
                                    "history_event_credit_debtor"
                                )
                            }
                            title={"Último Evento de Crédito Devedor"}
                            sx={{flex: 1}}
                        >
                            {state.isEmpty.ultimoEventoCreditoDevedor ? (
                                <Grid item>
                                    <EmptyLabelSectionItem title="evento de crédito devedor"/>
                                </Grid>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item md={6} lg={5}>
                                            <LabelSectionItem
                                                title="DATA REFERENCIA"
                                                description={
                                                    state
                                                        .ultimoEventoCreditoDevedor
                                                        ?.data_referencia
                                                }
                                            />
                                        </Grid>
                                        <Grid item md={6} lg={4}>
                                            <LabelSectionItem
                                                title="SCORE EVENTO"
                                                description={
                                                    formatNumber(
                                                        state
                                                            .ultimoEventoCreditoDevedor
                                                            ?.score_evento, 4)
                                                }
                                            />
                                        </Grid>
                                        <Grid item md={8} lg={8}>
                                            <LabelSectionItem
                                                title="EVENTO CREDITO"
                                                description={
                                                    state
                                                        .ultimoEventoCreditoDevedor
                                                        ?.evento_credito_devedor
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </EmissionSections>
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}

BoxSectionRating.propTypes = {
    cetipIsin: PropTypes.string,
    emissaoService: PropTypes.any,
    characterData: PropTypes.any,
    admin: PropTypes.bool,
}
